import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Inbox,
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Ban,
  Clock,
  Briefcase,
  MapPin,
  DollarSign,
  Calendar,
  Building2,
  AlertCircle
} from 'lucide-react';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import { formatEmploymentInfo } from '@/utils/jobOfferUtils';
import Input from '@/components/common/Input';
import Select from '@/components/common/Select';
import { candidateJobOfferService } from '@/services/jobOfferService';
import { toast } from '@/stores/toastStore';
import { JobOffer, JobOfferStatus } from '@/types';
import { useAuth } from '@/stores/authStore';

export default function ReceivedJobOffers() {
  const { t } = useTranslation('jobOffers');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [offers, setOffers] = useState<JobOffer[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0,
    viewRate: 0,
    responseRate: 0,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState<JobOfferStatus | ''>('');
  const [searchQuery, setSearchQuery] = useState('');

  // Only fetch data if user has given consent
  useEffect(() => {
    // Don't fetch any data until user has given consent
    if (user?.dataProcessingConsent !== true) {
      console.log('[ReceivedJobOffers] User has not given consent - skipping data fetch');
      setLoading(false);
      return;
    }

    console.log('[ReceivedJobOffers] User has given consent - fetching offers...');
    fetchOffers();
  }, [statusFilter, pagination.page, user?.dataProcessingConsent]);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await candidateJobOfferService.listJobOffers({
        status: statusFilter || undefined,
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      setOffers(response.offers);
      setPagination(response.pagination);
      setStats({
        total: response.stats.total,
        pending: response.stats.pending,
        accepted: response.stats.accepted,
        rejected: response.stats.rejected,
        viewRate: response.stats.viewRate ?? 0,
        responseRate: response.stats.responseRate ?? 0,
      });
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('offers.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: JobOfferStatus) => {
    switch (status) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'VIEWED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ACCEPTED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'REJECTED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'WITHDRAWN':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'EXPIRED':
        return 'bg-gray-100 text-gray-600 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: JobOfferStatus) => {
    switch (status) {
      case 'PENDING':
        return <Clock className="w-4 h-4" />;
      case 'VIEWED':
        return <Eye className="w-4 h-4" />;
      case 'ACCEPTED':
        return <CheckCircle className="w-4 h-4" />;
      case 'REJECTED':
        return <XCircle className="w-4 h-4" />;
      case 'WITHDRAWN':
        return <Ban className="w-4 h-4" />;
      case 'EXPIRED':
        return <Clock className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const canRespondToOffer = (offer: JobOffer) => {
    if (offer.status !== 'PENDING' && offer.status !== 'VIEWED') return false;
    if (offer.expiresAt && new Date(offer.expiresAt) < new Date()) return false;
    return true;
  };

  const isExpiringSoon = (expiresAt?: string) => {
    if (!expiresAt) return false;
    const daysUntilExpiry = Math.ceil((new Date(expiresAt).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 3 && daysUntilExpiry > 0;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatSalary = (min?: number, max?: number, currency?: string) => {
    if (!min && !max) return null;
    const curr = currency || 'USD';
    if (min && max) return `${min.toLocaleString()} - ${max.toLocaleString()} ${curr}`;
    if (min) return `${min.toLocaleString()}+ ${curr}`;
    if (max) return `Up to ${max.toLocaleString()} ${curr}`;
    return null;
  };

  const filteredOffers = offers.filter(offer => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      offer.position.toLowerCase().includes(query) ||
      offer.companyName?.toLowerCase().includes(query) ||
      offer.location?.toLowerCase().includes(query)
    );
  });

  const statusOptions = [
    { value: '', label: t('offers.filters.allStatuses') },
    { value: 'PENDING', label: t('offers.status.pending') },
    { value: 'VIEWED', label: t('offers.status.viewed') },
    { value: 'ACCEPTED', label: t('offers.status.accepted') },
    { value: 'REJECTED', label: t('offers.status.rejected') },
    { value: 'WITHDRAWN', label: t('offers.status.withdrawn') },
    { value: 'EXPIRED', label: t('offers.status.expired') },
  ];

  return (
    <div className="container-custom py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('offers.received.title')}
          </h1>
          <p className="text-gray-600">
            {t('offers.received.subtitle')}
          </p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card padding="sm">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
              <Inbox className="w-4 h-4 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 leading-none mb-1">{stats.total}</p>
            <p className="text-xs text-gray-500">{t('offers.stats.total')}</p>
          </Card>

          <Card padding="sm">
            <div className="w-8 h-8 bg-yellow-50 rounded-lg flex items-center justify-center mb-3">
              <Clock className="w-4 h-4 text-yellow-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 leading-none mb-1">{stats.pending}</p>
            <p className="text-xs text-gray-500">{t('offers.stats.pending')}</p>
          </Card>

          <Card padding="sm">
            <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center mb-3">
              <CheckCircle className="w-4 h-4 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 leading-none mb-1">{stats.accepted}</p>
            <p className="text-xs text-gray-500">{t('offers.stats.accepted')}</p>
          </Card>

          <Card padding="sm">
            <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center mb-3">
              <XCircle className="w-4 h-4 text-red-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 leading-none mb-1">{stats.rejected}</p>
            <p className="text-xs text-gray-500">{t('offers.stats.rejected')}</p>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder={t('offers.filters.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={<Search className="w-5 h-5" />}
              />
            </div>
            <div className="md:w-64">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as JobOfferStatus | '')}
                options={statusOptions}
              />
            </div>
          </div>
        </Card>

        {/* Offers List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">{t('common.loading')}</p>
          </div>
        ) : filteredOffers.length === 0 ? (
          <Card className="text-center py-12">
            <Inbox className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('offers.received.empty.title')}
            </h3>
            <p className="text-gray-600">
              {t('offers.received.empty.description')}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredOffers.map((offer) => (
              <Card
                key={offer.id}
                className={`hover:shadow-md transition-shadow cursor-pointer ${
                  offer.isUpdated ? 'ring-2 ring-blue-500' : ''
                }`}
                onClick={() => navigate(`/candidate/job-offers/${offer.id}`)}
              >
                <div className="flex flex-col gap-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {offer.position}
                        </h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(offer.status)}`}>
                          {getStatusIcon(offer.status)}
                          {t(`offers.status.${offer.status.toLowerCase()}`)}
                        </span>
                        {offer.isUpdated && (
                          <span className="px-2 py-1 bg-blue-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            {t('offers.updated')}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-gray-600 mb-3">
                        <Building2 className="w-4 h-4" />
                        <span className="font-medium">{offer.companyName}</span>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        {offer.location && (
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            <span>{offer.location}</span>
                          </div>
                        )}
                        {formatSalary(offer.salaryMin, offer.salaryMax, offer.salaryCurrency) && (
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" />
                            <span>{formatSalary(offer.salaryMin, offer.salaryMax, offer.salaryCurrency)}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-1">
                          <Briefcase className="w-4 h-4" />
                          <span>{formatEmploymentInfo(offer.employmentType, offer.workMode)}</span>
                        </div>
                        {offer.expiresAt && (
                          <div className={`flex items-center gap-1 ${isExpiringSoon(offer.expiresAt) ? 'text-red-600 font-medium' : ''}`}>
                            <Calendar className="w-4 h-4" />
                            <span>{t('offers.expiresAt')}: {formatDate(offer.expiresAt)}</span>
                          </div>
                        )}
                      </div>

                      {isExpiringSoon(offer.expiresAt) && canRespondToOffer(offer) && (
                        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-red-600" />
                          <p className="text-sm text-red-700">
                            {t('offers.expiringSoon')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  {canRespondToOffer(offer) && (
                    <div className="flex gap-3 pt-4 border-t border-gray-200">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/candidate/job-offers/${offer.id}`);
                        }}
                        className="flex-1"
                      >
                        {t('offers.actions.viewDetails')}
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
            >
              {t('common.previous')}
            </Button>
            <span className="flex items-center px-4 text-sm text-gray-600">
              {t('common.pageOf', { current: pagination.page, total: pagination.totalPages })}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page === pagination.totalPages}
            >
              {t('common.next')}
            </Button>
          </div>
        )}
    </div>
  );
}
